import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map } from 'rxjs';

import { NotificationIntervals } from '@ninety/platform/models/billing/notification-intervals.enum';
import { SubscriptionConfigurationV3 } from '@ninety/platform/models/billing/subscription-configuration-v3';
import { SubscriptionFrequencies } from '@ninety/platform/models/billing/subscription-frequencies.enum';
import { FeatureFlagKeys, selectFeatureFlag, selectCurrentUserIsAdminOrOwner } from '@ninety/platform/state';
import { BillingStateActions } from '@ninety/platform/state/app-global/billing/billing-state.actions';
import {
  selectCurrentSubscriptionConfig,
  selectIsRenewingPaidAnnualSubscription,
  selectRenewalDate,
  selectRenewalNotificationDismissedKey,
} from '@ninety/platform/state/app-global/billing/billing-state.selectors';
import { TerraModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

@Component({
  selector: 'ninety-subscription-renewal-banner',
  templateUrl: './subscription-renewal-banner.component.html',
  styleUrls: ['./subscription-renewal-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, PushPipe, TerraModule, ButtonComponent, RouterLink],
})
export class SubscriptionRenewalBannerComponent {
  protected readonly intervals = NotificationIntervals;

  private readonly store = inject(Store);

  protected readonly renewalNotificationDismissedKey$ = this.store.select(selectRenewalNotificationDismissedKey);

  protected readonly currentUserAdminOrOwner$ = this.store.select(selectCurrentUserIsAdminOrOwner);
  protected readonly isRenewingPaidAnnualSubscription$ = this.store.select(selectIsRenewingPaidAnnualSubscription);
  protected readonly renewalRemindersEnabled$ = this.store.select(
    selectFeatureFlag(FeatureFlagKeys.inAppSubscriptionRenewalReminderBanners)
  );
  protected readonly shouldShowBanner$ = combineLatest([
    this.currentUserAdminOrOwner$,
    this.isRenewingPaidAnnualSubscription$,
    this.renewalRemindersEnabled$,
  ]).pipe(
    map(
      ([isAdminOrOwner, isRenewingPaidAnnual, remindersEnabled]) =>
        isAdminOrOwner && isRenewingPaidAnnual && remindersEnabled
    )
  );

  protected readonly renewalDate$ = this.store.select(selectRenewalDate);
  protected readonly currentSubscriptionConfig$ = this.store.select(selectCurrentSubscriptionConfig);
  protected readonly subscriptionName$ = this.currentSubscriptionConfig$.pipe(
    map(sc => this.subscriptionConfigurationToName(sc))
  );

  protected readonly data$ = combineLatest([
    this.renewalDate$,
    this.currentSubscriptionConfig$,
    this.subscriptionName$,
  ]).pipe(
    filter(([renewalDate, config, subscriptionName]) => !!renewalDate && !!subscriptionName && !!config),
    map(([renewalDate, config, subscriptionName]) => ({
      renewalDate,
      subscriptionName,
      config,
    }))
  );

  protected readonly show30DayBanner$ = combineLatest([this.renewalNotificationDismissedKey$, this.renewalDate$]).pipe(
    map(([dismissedKey, renewalDate]) => {
      return (
        dismissedKey !== this.formRenewalNotificationDismissalKey(NotificationIntervals.ThirtyDays, renewalDate) &&
        this.isWithinRenewalInterval(NotificationIntervals.ThirtyDays, NotificationIntervals.SevenDays, renewalDate)
      );
    })
  );

  private readonly notificationDismissedForSessionSubject = new BehaviorSubject<boolean>(
    sessionStorage?.getItem('renewalNotificationDismissed') === 'true'
  );

  protected readonly notificationDismissedForSession$ = this.notificationDismissedForSessionSubject.asObservable();

  dismissForSession(): void {
    sessionStorage.setItem('renewalNotificationDismissed', 'true');
    this.notificationDismissedForSessionSubject.next(true);
  }

  protected readonly show7DayBanner$ = combineLatest([this.notificationDismissedForSession$, this.renewalDate$]).pipe(
    map(([sessionNotificationDismissed, renewalDate]) => {
      return (
        !sessionNotificationDismissed &&
        this.isWithinRenewalInterval(NotificationIntervals.SevenDays, NotificationIntervals.ZeroDays, renewalDate)
      );
    })
  );

  reviewBilling(interval: NotificationIntervals, renewalDate: Date) {
    const notificationType = interval;
    const daysUntilRenewal = this.getDaysUntilRenewal(renewalDate);
    this.store.dispatch(BillingStateActions.renewalNotificationCTAClicked({ notificationType, daysUntilRenewal }));
  }

  dismissRenewalNotification(interval: NotificationIntervals, renewalDate: Date) {
    let key = this.formRenewalNotificationDismissalKey(interval, renewalDate);
    const notificationType = interval;
    const daysUntilRenewal = this.getDaysUntilRenewal(renewalDate);

    if (interval === NotificationIntervals.SevenDays) {
      key = '';
      this.dismissForSession();
    }

    this.store.dispatch(BillingStateActions.dismissRenewalNotification({ key, notificationType, daysUntilRenewal }));
  }

  formRenewalNotificationDismissalKey(notificationType: number, renewalDate: Date): string {
    return `${notificationType}_${renewalDate.toISOString()}`;
  }

  subscriptionConfigurationToName(sc: SubscriptionConfigurationV3): string {
    const helper = (s: SubscriptionFrequencies): string => {
      switch (s) {
        case 'year':
          return 'annual';
        case 'month':
          return 'monthly';
        default:
          return s;
      }
    };

    return `${sc.planLevel} ${helper(sc.frequency)}`;
  }

  getDaysUntilRenewal(renewalDate: Date, now: Date = new Date()): number {
    const diffInDays = this.getRenewalDiffInDays(renewalDate, now);
    return Math.max(diffInDays, 0);
  }

  // isWithinRenewalInterval intervalMax (days) is inclusive and intervalMin (days) is exclusive
  isWithinRenewalInterval(
    intervalMax: number,
    intervalMin: number,
    renewalDate: Date,
    now: Date = new Date()
  ): boolean {
    const diffInDays = this.getRenewalDiffInDays(renewalDate, now);

    return diffInDays <= intervalMax && diffInDays > intervalMin;
  }

  getRenewalDiffInDays(renewalDate: Date, now: Date = new Date()): number {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfRenewal = new Date(renewalDate.getFullYear(), renewalDate.getMonth(), renewalDate.getDate());

    return Math.round((startOfRenewal.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
  }
}
